/**
 * 学习乐园 · 记忆翻牌（game-fanpai.js）
 * ============================================================
 * 「记忆与观察」组第二作：牌全背面盖住，翻两张配对——连连看的姊妹作。
 * 与连连看的关键差异：那边牌面全明、练「配对 + 找路」；这边牌面全暗，
 * 练的是**位置记忆**——你得记住「刚才那个 6 翻开在第几行第几列」。
 *
 * 复用（零数据成本）：题材包与连连看同源——footer 按 engine='lianliankan'
 * 分流加载 lianliankan-pack-<id>.min.js（23 个题材，defer 文档序先于本文件），
 * 配对数据、note 讲解与「配错讲正确搭档」的口径全部现成，本引擎只做记忆层。
 *
 * 档位（默认档铁律——最简）：入门 4×3 = 12 张 6 对（默认档）/ 进阶 5×4 = 20 张
 * 10 对 / 挑战 6×4 = 24 张 12 对。
 *
 * 反馈口径（栏目底线——即时且可解释）：
 *   配错不惩罚，明说两张牌各是谁、正确搭档是谁（牌还盖着，本身就是线索）；
 *   配上把 note 里的口诀/释义亮出来——配上的一瞬就是学到的一瞬。
 *   「亮一眼」把没配对的牌面短暂全部亮出（每局限用，次数如实计入）。
 *
 * 记录：按题材 + 档位记通关次数 / 最少步数（两张一步）/ 最好用时，只存本机。
 *
 * 冒烟钩子：window.STFanpai 暴露 buildDeck / isMatch / missExplain / state()。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-fanpai';
  var RECORD_KEY = 'study-fanpai-record';
  var BAD_MS = 750;      // 配错红闪时长：两张都看清再盖回
  var PEEK_MS = 1300;    // 「亮一眼」亮出时长

  /* ============ 引擎：牌堆与判定（纯函数） ============ */

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // 抽 count 对生成牌数组（每对两张；tile.mate = 正确搭档牌面，配错反馈用）
  // 与连连看 buildDeck 同构：牌面来自题材包，引擎只认 pair id——题材解耦。
  function buildDeck(pairs, count) {
    var pool = pairs.slice();
    shuffle(pool);
    var n = Math.min(count, pool.length);
    var tiles = [];
    for (var i = 0; i < n; i++) {
      var p = pool[i];
      tiles.push({ pair: i, side: 'l', face: p.l, mate: p.r, note: p.note || '' });
      tiles.push({ pair: i, side: 'r', face: p.r, mate: p.l, note: p.note || '' });
    }
    // 成对 push 天然相邻（pair i 恒占 2i/2i+1）——不整副洗牌的话，从左到右挨着翻全对
    shuffle(tiles);
    return tiles;
  }

  // 两张翻开的是不是一对：pair 相同且不是同一张
  function isMatch(a, b) {
    return !!a && !!b && a !== b && a.pair === b.pair;
  }

  // 配对成功时说什么（关联型顺手把口诀/释义亮出来——配上就是学到的一瞬）
  function pairExplain(a, b) {
    if (a.note && a.face !== b.face) {
      return '配上了：「' + a.face + '」连「' + b.face + '」——' + a.note;
    }
    return '配上了：「' + a.face + '」一对。';
  }

  // 配错时说什么（栏目底线：配错必须说清正确搭档是谁）
  function missExplain(a, b) {
    var sameFace = a.face === b.face;
    var part = '「' + a.face + '」的正确搭档是「' + a.mate + '」';
    if (!sameFace) part += '，「' + b.face + '」的正确搭档是「' + b.mate + '」';
    return '这两张不是一对——' + part + '。牌还盖着，记住它们刚才在哪。';
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { rows: 3, cols: 4, name: '入门 4×3', hint: '默认档 · 6 对' },
    { rows: 4, cols: 5, name: '进阶 5×4', hint: '10 对' },
    { rows: 4, cols: 6, name: '挑战 6×4', hint: '12 对' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    packId: '', soundOn: true, pack: null,
    rows: 3, cols: 4,
    deck: [],            // cells[i] = tile | null（本作牌不会消掉，只会翻开）
    state: [],           // 'back' | 'up' | 'matched'
    first: -1,           // 第一张翻开的格号；-1 = 没选
    badIdx: [],          // 配错红闪中的两张格号（is-bad 双通道反馈）
    busy: false,         // 配错红闪 / 亮一眼期间锁点击
    moves: 0, misses: 0, hints: 0,
    matched: 0, pairs: 0,
    elapsed: 0, timer: null,
    ended: false,
    peekTimer: null, badTimer: null,
    resetArm: false, resetTimer: null
  };

  /* ---- 题材选择器（与连连看同构，读同一份 STLLK_PACKS） ---- */

  function renderPackButtons() {
    var box = ui.packs;
    box.textContent = '';
    var ids = Object.keys(global.STLLK_PACKS || {});
    if (!ids.length) {
      box.appendChild(el('p', 'st-llk-none', '题材包没有加载出来，刷新一次试试。'));
      return;
    }
    for (var i = 0; i < ids.length; i++) {
      (function (id) {
        var p = global.STLLK_PACKS[id];
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-pack', id);
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', p.name));
        b.appendChild(el('small', 'st-llk-pack-hint', p.hint));
        b.addEventListener('click', function () {
          if (S.packId === id) return;
          newGame(id);   // 换题材 = 直接开新局
        });
        box.appendChild(b);
      })(ids[i]);
    }
  }

  function syncPackButtons() {
    var btns = ui.packs.querySelectorAll('[data-pack]');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pack') === S.packId;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 档位选择器 ---- */

  function renderLevelButtons() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', lv.rows + 'x' + lv.cols);
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.rows === lv.rows && S.cols === lv.cols) return;
          S.rows = lv.rows; S.cols = lv.cols;
          newGame(S.packId);   // 换档 = 直接开新局
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevelButtons() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-level') === S.rows + 'x' + S.cols;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 棋盘 ---- */

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-fp-cols', String(S.cols));
    ui.cells = [];
    for (var i = 0; i < S.rows * S.cols; i++) {
      (function (idx) {
        var b = el('button', 'st-fp-card');
        b.type = 'button';
        b.appendChild(el('span', 'st-fp-back', '?'));
        b.appendChild(el('span', 'st-fp-face'));
        b.addEventListener('click', function () { onCard(idx); });
        board.appendChild(b);
        ui.cells.push(b);
      })(i);
    }
    renderCards();
  }

  // 牌面字号档：口径同连连看 faceClass（1 字最大、4 字以上最小；emoji 单独放大）
  function faceClass(face, kind) {
    var n = face.length;
    var len = n <= 1 ? '1' : (n === 2 ? '2' : (n === 3 ? '3' : '4'));
    return 'st-fp-face st-fp-face--' + len + (kind === 'same' ? ' st-fp-face--emoji' : '');
  }

  function renderCards() {
    var kind = S.pack && S.pack.kind;
    for (var i = 0; i < ui.cells.length; i++) {
      var btn = ui.cells[i];
      var tile = S.deck[i];
      var face = btn.querySelector('.st-fp-face');
      var up = S.state[i] !== 'back';
      var label = '第' + (Math.floor(i / S.cols) + 1) + '行第' + (i % S.cols + 1) + '列，';
      if (S.state[i] === 'matched') label += '已配对：' + tile.face;
      else if (up) label += '已翻开：' + tile.face;
      else label += '未翻开';
      if (S.first === i) label += '，已选中';
      btn.setAttribute('aria-label', label);
      face.className = faceClass(tile.face, kind);
      face.textContent = up ? tile.face : '';
      btn.classList.toggle('is-open', up);
      btn.classList.toggle('is-matched', S.state[i] === 'matched');
      btn.classList.toggle('is-first', S.first === i && S.state[i] === 'up');
      btn.classList.toggle('is-bad', S.badIdx.indexOf(i) >= 0);
      btn.disabled = S.state[i] === 'matched' || (S.busy && S.state[i] === 'back');
    }
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 / 按钮 ---- */

  function fmtTime(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒';
  }
  function fmtTimeShort(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒' : s + ' 秒';
  }

  function renderStatus() {
    if (S.ended) return;
    ui.status.className = 'st-status';
    ui.status.textContent = '剩 ' + (S.pairs - S.matched) + ' 对 · 已翻 ' + S.moves
      + ' 次 · 配错 ' + S.misses
      + (S.hints ? ' · 亮眼 ' + S.hints : '')
      + ' · 用时 ' + fmtTime(S.elapsed);
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function recKey() { return S.packId + ':' + S.rows + 'x' + S.cols; }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord()[recKey()];
    if (!rec || !rec.wins) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty',
        '这个题材这一档还没通关过——全部配对一次，这里记下你的最少步数。'));
      return;
    }
    box.className = 'st-llk-record';
    box.appendChild(recordStat('通关', rec.wins + ' 次'));
    box.appendChild(recordStat('最少步数', rec.bestMoves ? rec.bestMoves + ' 次' : '—'));
    box.appendChild(recordStat('最好用时', rec.bestTime ? fmtTimeShort(rec.bestTime) : '—'));
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.peekBtn.disabled = S.ended || S.busy || S.matched >= S.pairs;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置这个题材的成绩';
  }

  /* ---- 音效（WebAudio 本地合成，与点灯同一套音色表） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];   // sel
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 计时 ---- */

  function startTimer() {
    stopTimer();
    S.elapsed = 0;
    S.timer = setInterval(function () {
      if (S.ended) return;
      S.elapsed++;
      renderStatus();
    }, 1000);
  }
  function stopTimer() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  /* ---- 交互：翻牌与配对 ---- */

  function onCard(idx) {
    if (S.ended || S.busy) return;
    if (S.state[idx] === 'matched') return;
    if (S.state[idx] === 'up') {
      if (S.first === idx) {           // 再点自己 = 取消选中
        S.state[idx] = 'back';
        S.first = -1;
        renderCards();
        renderButtons();
      }
      return;
    }
    // 翻开一张背面牌
    S.state[idx] = 'up';
    sfx('sel');
    if (S.first < 0) {
      S.first = idx;
      renderCards();
      renderButtons();
      renderNote('翻开「' + S.deck[idx].face + '」——记住它的位置，再找它的搭档。', '');
      return;
    }
    // 第二张：判定配对
    var a = S.deck[S.first], b = S.deck[idx];
    S.moves++;
    if (isMatch(a, b)) {
      S.state[S.first] = 'matched';
      S.state[idx] = 'matched';
      S.matched++;
      S.first = -1;
      sfx('ok');
      renderCards();
      renderButtons();
      renderNote(pairExplain(a, b), 'ok');
      if (S.matched >= S.pairs) { winGame(); return; }
      renderStatus();
    } else {
      S.misses++;
      S.busy = true;
      sfx('bad');
      S.badIdx = [S.first, idx];
      S.first = -1;
      renderCards();
      renderButtons();
      renderNote(missExplain(a, b), 'bad');
      renderStatus();
      if (S.badTimer) clearTimeout(S.badTimer);
      S.badTimer = setTimeout(function () {
        S.badTimer = null;
        for (var i = 0; i < S.state.length; i++) {
          if (S.state[i] === 'up') S.state[i] = 'back';
        }
        S.badIdx = [];
        S.busy = false;
        renderCards();
        renderButtons();
      }, BAD_MS);
    }
  }

  /* ---- 亮一眼：没配对的牌面短暂全亮（次数如实计入） ---- */

  function peek() {
    if (S.ended || S.busy) return;
    if (S.first >= 0) {   // 先收起选中那张，避免与亮眼态混淆
      S.state[S.first] = 'back';
      S.first = -1;
    }
    S.hints++;
    S.busy = true;
    for (var i = 0; i < S.state.length; i++) {
      if (S.state[i] === 'back') {
        ui.cells[i].classList.add('is-peek');
        ui.cells[i].querySelector('.st-fp-face').textContent = S.deck[i].face;
      }
    }
    renderNote('都看仔细了——' + PEEK_MS / 1000 + ' 秒后盖回，靠刚才的印象去配。亮的次数会如实计入。', 'warn');
    renderStatus();
    renderButtons();
    if (S.peekTimer) clearTimeout(S.peekTimer);
    S.peekTimer = setTimeout(function () {
      S.peekTimer = null;
      S.busy = false;
      for (var j = 0; j < ui.cells.length; j++) ui.cells[j].classList.remove('is-peek');
      renderCards();
      renderButtons();
    }, PEEK_MS);
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    stopTimer();
    sfx('win');
    var rec = loadRecord();
    var key = recKey();
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0, bestMoves: 0, bestTime: 0 };
    if (typeof cur.wins !== 'number') cur.wins = 0;
    if (typeof cur.bestMoves !== 'number') cur.bestMoves = 0;
    if (typeof cur.bestTime !== 'number') cur.bestTime = 0;
    cur.wins++;
    var isRec = !cur.bestMoves || S.moves < cur.bestMoves;
    if (isRec) cur.bestMoves = S.moves;
    if (!cur.bestTime || S.elapsed < cur.bestTime) cur.bestTime = S.elapsed;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.rows + '×' + S.cols + ' 翻了 ' + S.moves
      + ' 次、配错 ' + S.misses + ' 次全配对' + (isRec ? '——这一档的新纪录！' : '（你这档纪录 ' + cur.bestMoves + ' 次）');
    renderNote('记忆翻牌的诀窍不是手快：翻开第一张前先回想「它的搭档刚才在第几行第几列」。位置记得住，配对就快。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置记忆翻牌的本机成绩');
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({
        v: 1, packId: S.packId, rows: S.rows, cols: S.cols, soundOn: S.soundOn
      }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.packId && global.STLLK_PACKS && global.STLLK_PACKS[d.packId]) S.packId = d.packId;
      if (d.rows === 3 && d.cols === 4) { S.rows = 3; S.cols = 4; }
      else if (d.rows === 4 && d.cols === 5) { S.rows = 4; S.cols = 5; }
      else if (d.rows === 4 && d.cols === 6) { S.rows = 4; S.cols = 6; }
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame(packId) {
    var pack = global.STLLK_PACKS && global.STLLK_PACKS[packId];
    if (!pack) return;
    if (S.badTimer) { clearTimeout(S.badTimer); S.badTimer = null; }
    if (S.peekTimer) { clearTimeout(S.peekTimer); S.peekTimer = null; }
    S.packId = packId;
    S.pack = pack;
    S.busy = false;
    S.badIdx = [];
    S.first = -1;
    S.pairs = S.rows * S.cols / 2;
    S.deck = buildDeck(pack.pairs, S.pairs);
    S.state = [];
    for (var i = 0; i < S.rows * S.cols; i++) S.state.push('back');
    S.moves = 0; S.misses = 0; S.hints = 0; S.matched = 0;
    S.ended = false;
    S.resetArm = false;
    syncPackButtons();
    syncLevelButtons();
    buildBoard();
    renderStatus();
    renderButtons();
    renderRecord();
    renderNote('「' + pack.name + '」' + S.rows + '×' + S.cols + ' 共 ' + S.pairs
      + ' 对全盖着。翻两张：配上了就亮着，配错了告诉你正确搭档是谁——靠记位置把整盘配完。', '');
    startTimer();
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.packs = root.querySelector('[data-st-fp-packs]');
    ui.levels = root.querySelector('[data-st-fp-levels]');
    ui.board = root.querySelector('[data-st-fp-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.peekBtn = root.querySelector('[data-st-peek]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    // 默认题材 = 算式配对（与连连看同款默认），恢复过偏好则用上次的
    var firstId = Object.keys(global.STLLK_PACKS || {})[0] || 'suanli';
    if (!S.packId) S.packId = global.STLLK_PACKS && global.STLLK_PACKS.suanli ? 'suanli' : firstId;
    restorePrefs();
    renderPackButtons();
    renderLevelButtons();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.packId); });
    ui.peekBtn.addEventListener('click', peek);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.packId);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STFanpai = {
    buildDeck: buildDeck,
    isMatch: isMatch,
    missExplain: missExplain,
    pairExplain: pairExplain,
    LEVELS: LEVELS,
    state: function () {
      return {
        packId: S.packId, rows: S.rows, cols: S.cols, deck: S.deck.slice(), states: S.state.slice(),
        first: S.first, moves: S.moves, misses: S.misses, hints: S.hints,
        matched: S.matched, pairs: S.pairs, elapsed: S.elapsed, ended: S.ended
      };
    }
  };
})(typeof window !== 'undefined' ? window : globalThis);
